import type { FunctionComponent, PropsWithChildren } from 'react';
import { useMemo } from 'react';

import { useHasRole } from '~/shared/context/use-has-role';
import { useTranslate } from '~/shared/context/use-translate';

import {
    anchorRootAttrs,
    BoundCommentsSurfaceProvider,
    type CommentDocumentState,
    CommentsClassificationPublisher,
    CommentsGroupMemberBinding,
    CommentsOpenRequestProvider,
} from '../../comments';
import { CommentsPermissionsContext } from '../../comments/comments-permissions-context';
import { workflowStepDocumentId } from '../../comments/document/comments-document';
import type { TaskManagementTaskDetail, TaskManagementTaskRef } from '../../data-contract/task-management';
import { useUrlTileEscapeClose, withCommentsOpen } from '../../url-tiles';
import { useTaskManagementTask } from '../api/use-task-management-task';
import { useJobArchitecturePermissions } from '../context/JobArchitecturePermissionsContext';
import { createTaskCommentsSubject } from '../profile-tasks/comments/profile-task-comments-adapter';
import { useProfileTaskCommentsApi } from '../profile-tasks/comments/profile-task-comments-api';
import { profileTaskRichTextToPlainText } from '../profile-tasks/profile-task-rich-text';
import { profileTaskTranslationKeys } from '../profile-tasks/profile-task-translations';
import { TaskDetailHeading } from '../profile-tasks/tile/profile-task-tile/TaskHeading';
import { TaskTileError, TaskTileLoading } from '../profile-tasks/tile/profile-task-tile/TaskTileFallbacks';
import { TaskTileFrame } from '../profile-tasks/tile/profile-task-tile/TaskTileFrame';
import { useJobArchitectureContentLanguage } from '../providers/JobArchitectureLanguageProvider';
import { WorkTaskTileFields } from './WorkTaskTileFields';
import { useWorkTaskTileSections } from './work-task-tile-sections';
import type { WorkTaskTilePayload } from './work-task-tile-url';

// A lead only ever loads tasks the backend scoped to them, so a loaded task is one they may comment on.
const useCanCommentOnWorkTask = (): boolean => {
    const { canRead } = useJobArchitecturePermissions();
    const isTransformationLead = useHasRole('transformation_lead');

    return canRead || isTransformationLead;
};

export const WorkTaskCommentsPermissionsProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const canComment = useCanCommentOnWorkTask();
    const value = useMemo(() => ({ canResolveThreads: canComment }), [canComment]);

    return <CommentsPermissionsContext.Provider value={value}>{children}</CommentsPermissionsContext.Provider>;
};

// Publishes the readable document so a comment can be classified against the section, skill or
// workflow step it was written on, exactly like the profile-task tile does for the same task.
const WorkTaskCommentsPublisher: FunctionComponent<{ readonly task: TaskManagementTaskDetail }> = ({ task }) => {
    const translate = useTranslate();
    const documentState = useMemo<CommentDocumentState>(() => {
        const description = profileTaskRichTextToPlainText(task.description);

        return {
            sections:
                description === undefined || description.length === 0
                    ? []
                    : [
                          {
                              id: 'description',
                              text: description,
                              title: translate(profileTaskTranslationKeys.description),
                          },
                      ],
            skills: task.requiredSkills.map(({ id, title }) => ({ id, title })),
            workflowSteps: (task.workflow?.steps ?? []).map((step, index) => ({
                id: workflowStepDocumentId(index),
                title: step.title,
            })),
        };
    }, [task, translate]);

    return <CommentsClassificationPublisher documentState={documentState} />;
};

const LoadedWorkTaskTile: FunctionComponent<{
    readonly onClose: () => void;
    readonly onOpenTask: (task: TaskManagementTaskRef) => void;
    readonly onUpdatePayload: (payload: WorkTaskTilePayload) => void;
    readonly payload: WorkTaskTilePayload;
    readonly task: TaskManagementTaskDetail;
}> = ({ onClose, onOpenTask, onUpdatePayload, payload, task }) => {
    const contentLanguage = useJobArchitectureContentLanguage();
    const commentsApi = useProfileTaskCommentsApi();
    const canComment = useCanCommentOnWorkTask();
    const activeLanguage = payload.language ?? contentLanguage;
    const sections = useWorkTaskTileSections();
    const isCommentsOpen = payload.isCommentsOpen === true;
    const comments = {
        isOpen: isCommentsOpen,
        onToggle: () => onUpdatePayload(withCommentsOpen(payload, !isCommentsOpen)),
    };
    const requestCommentsOpen = (): void => {
        if (!isCommentsOpen) {
            onUpdatePayload(withCommentsOpen(payload, true));
        }
    };
    // The rail is addressed by task id and integer version, the same subject the profile-task tile
    // binds, so a thread written on either tile shows up on both.
    const subject = createTaskCommentsSubject({
        architectureId: '',
        canComment,
        language: activeLanguage,
        taskId: task.id,
        taskVersionId: String(task.version),
    });
    const body = (
        <TaskTileFrame
            comments={canComment ? comments : undefined}
            contentAttrs={anchorRootAttrs(task.id)}
            testId="work-task-detail"
            onClose={onClose}
        >
            {canComment ? <WorkTaskCommentsPublisher task={task} /> : null}
            <TaskDetailHeading commentItemId={canComment ? task.id : undefined} title={task.title} />
            {/* Page sections continue the fields, so they keep the fields' section spacing. */}
            <div className="flex flex-col gap-6xl">
                <WorkTaskTileFields
                    activeLanguage={activeLanguage}
                    commentable={canComment ? { itemId: task.id, newThreadCreationDisabled: false } : undefined}
                    task={task}
                    onOpenTask={onOpenTask}
                />
                {sections?.(task)}
            </div>
        </TaskTileFrame>
    );

    if (!canComment) {
        return body;
    }

    return (
        <WorkTaskCommentsPermissionsProvider>
            <BoundCommentsSurfaceProvider api={commentsApi} subject={subject}>
                <CommentsOpenRequestProvider requestCommentsOpen={requestCommentsOpen}>
                    <CommentsGroupMemberBinding
                        api={commentsApi}
                        label={task.title}
                        memberRole="task"
                        subject={subject}
                    />
                    {body}
                </CommentsOpenRequestProvider>
            </BoundCommentsSurfaceProvider>
        </WorkTaskCommentsPermissionsProvider>
    );
};

export const WorkTaskTile: FunctionComponent<{
    readonly onClose: () => void;
    readonly onOpenTask: (task: TaskManagementTaskRef) => void;
    readonly onUpdatePayload: (payload: WorkTaskTilePayload) => void;
    readonly payload: WorkTaskTilePayload;
}> = ({ onClose, onOpenTask, onUpdatePayload, payload }) => {
    const translate = useTranslate();
    const query = useTaskManagementTask({ taskId: payload.taskId, version: payload.version });
    useUrlTileEscapeClose(onClose);

    if (query.isLoading) {
        return (
            <TaskTileFrame testId="work-task-loading" onClose={onClose}>
                <TaskTileLoading />
            </TaskTileFrame>
        );
    }

    if (query.isError || query.data === undefined) {
        return (
            <TaskTileFrame testId="work-task-error" onClose={onClose}>
                <TaskTileError message={translate(profileTaskTranslationKeys.errorBody)} />
            </TaskTileFrame>
        );
    }

    return (
        <LoadedWorkTaskTile
            payload={payload}
            task={query.data}
            onClose={onClose}
            onOpenTask={onOpenTask}
            onUpdatePayload={onUpdatePayload}
        />
    );
};
